import { API_ROOT } from '../../constants'
import api from '../../utils/api'
import axios from 'axios'
import JSZip from 'jszip'
import { uniqueNamesGenerator, colors, names, NumberDictionary } from 'unique-names-generator'
import { isVideoFile } from 'helpers/file-type'
import { getFilesMetadata, handlePresignedUploads } from '../presigned-uploads/handlePresignedUploads'
import { createSite, updateSite } from 'services/site'

export const MAX_DIRECT_UPLOAD_SIZE = 2 * 1024 * 1024 * 1024 // 2GB

export const generateRandomSubdomain = () => {
  const numberDictionary = NumberDictionary.generate({ min: 1, max: 99 })
  return uniqueNamesGenerator({
    dictionaries: [colors, names, numberDictionary],
    separator: '-',
    style: 'lowerCase'
  })
}

export const calculateTotalSize = async (files) => {
  let totalSize = 0

  if (!files || !Array.isArray(files)) {
    return 0
  }

  for (const file of files) {
    if (!file) continue
    if (file?.name?.toLowerCase().endsWith('.zip')) {
      try {
        const zip = new JSZip()
        const zipContents = await zip.loadAsync(file)

        for (const zipEntry of Object.values(zipContents.files)) {
          if (!zipEntry.dir) {
            totalSize += zipEntry._data.uncompressedSize
          }
        }
      } catch (error) {
        totalSize += file.size
      }
    } else {
      totalSize += file.size
    }
  }

  return totalSize
}

export const updateSiteContents = async (
  domain,
  body,
  config,
  uploadProgress,
  isPresignedUpload,
  originalFiles,
  dispatch
) => {
  try {
    const formData = body
    const filesToAdd = originalFiles || []
    const newDomain = formData.get('newDomain')

    const totalSize = await calculateTotalSize(filesToAdd)
    const totalFilesMetadata = {
      totalFiles: getFilesMetadata(filesToAdd),
      completedFiles: []
    }

    /** Trigger presigned upload flow for all video files */
    const isVideoFiles = filesToAdd.some((file) => isVideoFile(file.name, file.type))

    if (isPresignedUpload || totalSize > MAX_DIRECT_UPLOAD_SIZE || isVideoFiles) {
      formData.append('domain', domain)

      const [firstFile, ...remainingFiles] = filesToAdd

      const result = await handlePresignedUploads({
        files: firstFile ? [firstFile] : [],
        domain,
        newDomain,
        formData,
        config,
        isUpdate: true,
        isUpdateSite: true,
        uploadProgress,
        isLastFile: filesToAdd.length === 1 ? true : false,
        filesToAddPresignedTotalSize: totalSize,
        filesToAddPresignedMetadata: totalFilesMetadata,
        dispatch
      })

      totalFilesMetadata.completedFiles = getFilesMetadata([firstFile])

      if (remainingFiles.length > 0) {
        handlePresignedUploads({
          files: remainingFiles,
          domain,
          newDomain,
          formData,
          config,
          isUpdate: true,
          isUpdateSite: true,
          filesToAddPresignedTotalSize: totalSize,
          filesToAddPresignedMetadata: totalFilesMetadata,
          dispatch
        })
      }

      return result
    }

    return updateSite(body, { ...config, domain }, (event) => {
      const { total, loaded } = event
      uploadProgress({ total, loaded }, { size: total })
    })
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const createSiteContents = async (body, config, uploadProgress, dispatch) => {
  try {
    const formData = body
    const filesToAdd = formData.getAll('filesToAdd')
    const domainSuffix = formData.get('domainSuffix')
    const domain =
      formData.get('newDomain') || generateRandomSubdomain() + (domainSuffix ? domainSuffix : '.tiiny.site')

    const totalSize = await calculateTotalSize(filesToAdd)
    const totalFilesMetadata = {
      totalFiles: getFilesMetadata(filesToAdd),
      completedFiles: []
    }

    /** Trigger presigned upload flow for all video files */
    const isVideoFiles = filesToAdd.some((file) => isVideoFile(file.name, file.type))

    if (totalSize > MAX_DIRECT_UPLOAD_SIZE || isVideoFiles) {
      const [firstFile, ...remainingFiles] = filesToAdd

      const result = await handlePresignedUploads({
        files: [firstFile],
        domain,
        newDomain: domain,
        formData,
        config,
        isUpdate: false,
        isUpdateSite: false,
        uploadProgress,
        isLastFile: filesToAdd.length === 1 ? true : false,
        filesToAddPresignedTotalSize: totalSize,
        filesToAddPresignedMetadata: totalFilesMetadata,
        dispatch
      })

      totalFilesMetadata.completedFiles = getFilesMetadata([firstFile])

      if (remainingFiles.length > 0) {
        handlePresignedUploads({
          files: remainingFiles,
          domain,
          newDomain: domain,
          formData,
          config,
          isUpdate: true,
          isUpdateSite: false,
          filesToAddPresignedTotalSize: totalSize,
          filesToAddPresignedMetadata: totalFilesMetadata,
          dispatch
        })
      }

      return result
    }

    return createSite(body, config, (event) => {
      const { total, loaded } = event
      uploadProgress({ total, loaded }, { size: total })
    })
  } catch (error) {
    console.error(error)
    throw error
  }
}
